import { useQuery } from "@tanstack/react-query";
import client from "api/graphql/getClient";
import { streamIdPlaceholder } from "common/apiDefinitions";
import { graphql } from "gql/gql";
import { ListAlertsParams, SortDirection } from "gql/graphql";
import { useActiveAccount } from "utils/useActiveAccount";
import useSnackbar from "utils/useSnackbar";

const listAlertsSummaryDocument = graphql(/* GraphQL */ `
	query listAlertsSummary($params: ListAlertsParams!) {
		listAlerts(params: $params) {
			__typename
			_id
			created
			stream_id
		}
	}
`);

interface Options {
	refetchIntervalInBackground?: boolean;
}

const listAlertsSummary = async (params: ListAlertsParams, userId?: string) => {
	const updatedParams = {
		...params,
		before: new Date().toISOString(),
		sort_dir: params.sort_dir ?? SortDirection.DESC,
		stream_ids: params.stream_ids.filter((_id) => _id !== streamIdPlaceholder),
	};

	const res = await client(userId).request(listAlertsSummaryDocument, {
		params: updatedParams,
	});
	return res.listAlerts;
};

const useListAlertsSummary = (
	params: ListAlertsParams,
	{ refetchIntervalInBackground = false }: Options = {}
) => {
	const { _id, userId } = useActiveAccount();
	const { handleError } = useSnackbar();

	return useQuery(
		listAlertsSummaryQueryKey(
			userId,
			_id,
			params.stream_ids,
			params.after,
			params.count
		),
		() => listAlertsSummary(params, userId),
		{
			enabled: !!userId && params.stream_ids.length > 0,
			onError: (error) => handleError(error),
			refetchInterval: 30 * 1000,
			refetchIntervalInBackground,
			refetchOnWindowFocus: true,
		}
	);
};

export default useListAlertsSummary;

export const listAlertsSummaryQueryKey = (
	userId: string | undefined,
	_id: string | undefined,
	streamIds: string[],
	after?: string | null,
	count?: number | null
) => {
	return [
		"user",
		userId,
		"account",
		_id,
		"alertsSummary",
		streamIds,
		"after",
		after,
		"count",
		count,
	];
};
